import type {
  CourseDocument,
  CourseDocumentPoint3,
  HoleDocument,
  PlacementDocument,
  RegionDocument,
} from '../CourseDocument';
import type { Region, Vec2 } from '../../utils/geometry';

export type Firmness = 'soft' | 'medium-soft' | 'medium-firm' | 'firm';
export interface EnvironmentPalette {
  fairway: string;
  rough: string;
  bunker: string;
  water: string;
  haze: string;
  sky?: string;
  rock?: string;
  wasteArea?: string;
}
export interface EventPresentation {
  accentColor: string;
  mark: string;
  subtitle: string;
  loadingCopy: string;
  identityLine: string;
  tierLabel: string;
}
export interface CourseProductionMetadata {
  location: string;
  regionType: string;
  difficulty: number;
  par: number;
  yardage: number;
  typicalWind: readonly [number, number];
  greenSpeed: readonly [number, number];
  firmness: Firmness;
  primaryChallenge: string;
  signatureHoles: readonly number[];
  tournamentEventId: string;
  previewTheme: string;
  environmentPalette: EnvironmentPalette;
  vegetationStyle: string;
  terrainStyle: string;
  sunlightDirection?: string;
  distantScenery?: string;
  waterStyle?: string;
  eventPresentation?: EventPresentation;
  /** Broad native sand uses a lighter lie penalty than maintained bunkers. */
  wasteArea?: { carryMultiplier: number; consistencyMultiplier: number; rollMultiplier: number };
}
export interface CourseBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}
export interface ElevationProfile {
  tee: number;
  landing: number;
  green: number;
}
export interface LandingZone {
  distance: number;
  width: number;
  preferredSide?: 'left' | 'right';
}
export type Dogleg = 'left' | 'right' | 'double' | 'none';
export interface HolePlan {
  par: number;
  yardage: number;
  name: string;
  heading: number;
  dogleg?: Dogleg;
  elevation?: ElevationProfile;
  landingZones?: LandingZone[];
  water?: 'left' | 'right' | 'carry' | 'green';
  splitFairway?: boolean;
  greenRadius?: number;
  bunkerCount?: number;
  trees?: number;
  rocks?: number;
}
export interface TerrainPreset {
  id: string;
  amplitude: number;
  wavelength: number;
  roughScale: number;
}
export const terrainPreset = (
  id: string,
  amplitude: number,
  wavelength: number,
  roughScale = 1,
): TerrainPreset => ({ id, amplitude, wavelength, roughScale });
export const courseBounds = (
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
): CourseBounds => ({ minX, maxX, minZ, maxZ });
export const teeBox = (id: string, position: CourseDocumentPoint3, name = 'Championship') => ({
  id,
  name,
  position,
});
export const pinPosition = (id: string, position: CourseDocumentPoint3, label = 'Tournament') => ({
  id,
  label,
  position,
});
export const circle = (center: Vec2, radius: number): Region => ({
  kind: 'circle',
  center,
  radius,
});
export const freeformFairway = (id: string, points: Vec2[]): RegionDocument => ({
  id,
  region: { kind: 'polygon', points },
});
export const green = (id: string, center: Vec2, radius = 16): RegionDocument => ({
  id,
  region: circle(center, radius),
});
export const bunker = (id: string, center: Vec2, radius = 7): RegionDocument => ({
  id,
  region: circle(center, radius),
});
export const waterRegion = (id: string, points: Vec2[], waterLevel = 0.04): RegionDocument => ({
  id,
  region: { kind: 'polygon', points },
  waterLevel,
});
export const rock = (
  id: string,
  position: CourseDocumentPoint3,
  radius = 2,
): PlacementDocument => ({ id, kind: 'rock', position, radius, scale: radius / 2 });
export const elevationProfile = (
  tee: number,
  landing: number,
  greenHeight: number,
): ElevationProfile => ({ tee, landing, green: greenHeight });
export const landingZone = (
  distance: number,
  width: number,
  preferredSide?: 'left' | 'right',
): LandingZone => ({ distance, width, preferredSide });
export const dogleg = (direction: Dogleg): Dogleg => direction;

export function centerlineFairway(id: string, line: Vec2[], widths: number[]): RegionDocument {
  const left: Vec2[] = [],
    right: Vec2[] = [];
  line.forEach((p, i) => {
    const before = line[Math.max(0, i - 1)],
      after = line[Math.min(line.length - 1, i + 1)];
    const dx = after.x - before.x,
      dz = after.z - before.z,
      length = Math.hypot(dx, dz) || 1;
    const nx = -dz / length,
      nz = dx / length,
      half = (widths[i] ?? widths.at(-1) ?? 30) / 2;
    left.push({ x: p.x + nx * half, z: p.z + nz * half });
    right.push({ x: p.x - nx * half, z: p.z - nz * half });
  });
  return freeformFairway(id, [...left, ...right.reverse()]);
}

export function vegetationCorridor(
  prefix: string,
  line: Vec2[],
  count: number,
  clearance = 34,
): PlacementDocument[] {
  const result: PlacementDocument[] = [];
  for (let i = 0; i < count; i++) {
    const t = (i + 1) / (count + 1),
      a = line[Math.min(line.length - 2, Math.floor(t * (line.length - 1)))],
      b = line[Math.min(line.length - 1, Math.floor(t * (line.length - 1)) + 1)];
    const side = i % 2 ? 1 : -1,
      x = a.x + (b.x - a.x) * t,
      z = a.z + (b.z - a.z) * t;
    const dx = b.x - a.x,
      dz = b.z - a.z,
      length = Math.hypot(dx, dz) || 1;
    result.push({
      id: `${prefix}-${i}`,
      kind: 'tree',
      position: {
        x: x + (-dz / length) * clearance * side,
        z: z + (dx / length) * clearance * side,
      },
      radius: 1.5,
      scale: 1 + (i % 3) * 0.12,
    });
  }
  return result;
}

export function greenComplex(id: string, center: Vec2, radius: number, bunkerCount: number) {
  const bunkers = Array.from({ length: bunkerCount }, (_, i) => {
    const angle = (i / Math.max(1, bunkerCount)) * Math.PI * 2 + 0.5;
    return bunker(
      `${id}-b${i + 1}`,
      {
        x: center.x + Math.cos(angle) * (radius + 10),
        z: center.z + Math.sin(angle) * (radius + 10),
      },
      6,
    );
  });
  return { green: green(`${id}-green`, center, radius), bunkers };
}

const pointAlong = (start: Vec2, heading: number, distance: number): Vec2 => ({
  x: start.x + Math.sin(heading) * distance,
  z: start.z + Math.cos(heading) * distance,
});
export function holeDefinition(number: number, plan: HolePlan, start: Vec2): HoleDocument {
  const metres = plan.yardage * 0.9144,
    bend = plan.dogleg === 'left' ? -1 : plan.dogleg === 'right' ? 1 : 0;
  const mid = pointAlong(start, plan.heading, metres * 0.55),
    lateral = { x: Math.cos(plan.heading) * bend * 42, z: -Math.sin(plan.heading) * bend * 42 };
  mid.x += lateral.x;
  mid.z += lateral.z;
  const end = pointAlong(mid, plan.heading + bend * 0.16, metres * 0.45);
  const line = [start, mid, end],
    width = plan.splitFairway ? 58 : (plan.landingZones?.[0]?.width ?? (plan.par === 3 ? 24 : 38));
  const complex = greenComplex(
    `h${number}`,
    end,
    plan.greenRadius ?? (plan.par === 3 ? 18 : 16),
    plan.bunkerCount ?? 2,
  );
  const water: RegionDocument[] = [];
  if (plan.water) {
    const side = plan.water === 'left' ? -1 : 1,
      anchor = plan.water === 'carry' ? mid : end;
    const ox = Math.cos(plan.heading) * side * (plan.water === 'green' ? 20 : 30),
      oz = -Math.sin(plan.heading) * side * (plan.water === 'green' ? 20 : 30);
    water.push({
      id: `h${number}-water`,
      region: circle({ x: anchor.x + ox, z: anchor.z + oz }, plan.water === 'carry' ? 22 : 18),
      waterLevel: 0.04,
    });
  }
  const trees = vegetationCorridor(`h${number}-tree`, line, plan.trees ?? 4, width / 2 + 15);
  const props = Array.from({ length: plan.rocks ?? 0 }, (_, i) =>
    rock(
      `h${number}-rock-${i}`,
      { x: mid.x + (i % 2 ? 1 : -1) * (width + 10), z: mid.z + i * 8 },
      2 + (i % 2),
    ),
  );
  return {
    id: `hole-${number}`,
    number,
    par: plan.par,
    yardage: plan.yardage,
    name: plan.name,
    teeBoxes: [teeBox(`h${number}-tee`, { ...start, y: plan.elevation?.tee ?? 0 })],
    pins: [pinPosition(`h${number}-pin`, { ...end, y: plan.elevation?.green ?? 0 }, plan.name)],
    activePinIndex: 0,
    fairways: [centerlineFairway(`h${number}-fairway`, line, [width * 0.7, width, width * 0.65])],
    greens: [complex.green],
    roughRegions: [{ id: `h${number}-rough`, region: circle(mid, metres * 0.58) }],
    bunkers: complex.bunkers,
    waterRegions: water,
    vegetation: trees,
    props,
  };
}

export interface CourseBlueprint {
  id: string;
  name: string;
  description: string;
  environmentPreset: string;
  metadata: Omit<CourseProductionMetadata, 'par' | 'yardage'>;
  plans: HolePlan[];
}
export function authorCourse(blueprint: CourseBlueprint): CourseDocument {
  const holes = blueprint.plans.map((plan, i) => {
    const row = Math.floor(i / 3),
      col = i % 3;
    return holeDefinition(i + 1, plan, { x: col * 680 + (row % 2) * 90, z: row * 760 });
  });
  return {
    schemaVersion: 2,
    id: blueprint.id,
    name: blueprint.name,
    description: blueprint.description,
    environmentPreset: blueprint.environmentPreset,
    holes,
    production: {
      ...blueprint.metadata,
      par: holes.reduce((s, h) => s + h.par, 0),
      yardage: holes.reduce((s, h) => s + h.yardage, 0),
    },
  };
}
